import { apiBaseUrl, authToken } from '../lib/api';
import { downloadFile, isTelegram } from '../lib/telegram';
export async function adminRequest<T>(
  path: string,
  method = 'GET',
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const token = authToken();
  const response = await fetch(`${apiBaseUrl}/admin${path}`, {
    method,
    credentials: 'same-origin',
    signal,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error?.message ?? 'Не удалось загрузить данные');
  return data as T;
}
/** Кружок для рассылки: сырой mp4 в теле, в ответ file_id (видео при этом приходит загрузившему). */
export async function adminUploadVideoNote(file: File, length: number, duration: number): Promise<string> {
  const token = authToken();
  const response = await fetch(`${apiBaseUrl}/admin/campaign-video-note?length=${length}&duration=${duration}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'video/mp4', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: file,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error?.message ?? 'Не удалось загрузить видео');
  return (data as { fileId: string }).fileId;
}
/**
 * Внутри Telegram файл скачивает сам клиент (downloadFile) по короткой одноразовой
 * ссылке: blob-ссылки WebView Telegram открывает как внешнюю программу.
 */
export async function adminDownloadUsers(q: string, segment: string): Promise<void> {
  if (isTelegram) {
    const link = await adminRequest<{ url: string; filename: string }>('/users.csv/link', 'POST', { q, segment });
    if (!(await downloadFile(link.url, link.filename))) throw new Error('Скачивание отменено');
    return;
  }
  await adminDownload(`/users.csv?q=${encodeURIComponent(q)}&segment=${segment}`, `users-${segment}.csv`);
}
/** Скачивание файла с тем же входом, что и запросы: cookie в браузере или токен. */
export async function adminDownload(path: string, filename: string): Promise<void> {
  const token = authToken();
  const response = await fetch(`${apiBaseUrl}/admin${path}`, {
    credentials: 'same-origin',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error('Не удалось скачать файл');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
}
export interface Daily {
  day: string;
  registrations: number;
  payers: number;
  spent: number;
  trial: number;
  paid_videos: number;
  started: number;
  failed: number;
  creators: number;
  revenue_rub: number;
  revenue_stars: number;
  cost_usd: number;
}
export interface Overview {
  daily: Daily[];
  payments: { currency: string; status: string; amount: number; count: number }[];
  current: { users: number; subscribers: number; reserved: number; payers: number };
  timezone: string;
}
export interface Insights {
  funnel: {
    registered: number;
    created_project: number;
    started: number;
    got_video: number;
    repeat_video: number;
    paid: number;
    median_hours_to_video: number | null;
  };
  production: {
    started: number;
    done: number;
    failed: number;
    in_progress: number;
    creators: number;
    median_s: number | null;
    p90_s: number | null;
    projects: number;
    abandoned: number;
  };
  errors: { error: string; count: number }[];
  costs: { step: string; provider: string; usd: number }[];
  subscriptions: {
    plans: { package_id: string; provider: string; renewing: number; cancelled: number }[];
    mrr: { stars: number; rub: number };
    first_payments: number;
    repeat_payments: number;
    churned: number;
  };
  activity: { dau: number; wau: number; mau: number };
  cohorts: { week: string; size: number; active: number[] }[];
  sources: {
    source: 'organic' | 'referral';
    registered: number;
    got_video: number;
    paid: number;
  }[];
  referrers: {
    id: string;
    tg_id: number;
    username: string | null;
    invited: number;
    paid: number;
  }[];
  settings: { key: string; value: string; count: number }[];
}
export interface Person {
  id: string;
  tg_id: number;
  username: string | null;
  created_at: string;
  package_id: string | null;
  period_end: string | null;
  cancelled: boolean;
  spent: number;
  trial: number;
  payments: number;
  promo_code: string | null;
  total: number;
}
export interface Detail {
  user: Person;
  payments: {
    id: string;
    provider: string;
    package_id: string;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
  }[];
  subscriptions: {
    package_id: string;
    provider: string;
    period_end: string;
    cancelled: boolean;
    revoked: boolean;
    superseded: boolean;
  }[];
  usage: {
    generation_id: string;
    status: string;
    kind: string | null;
    consumed_at: string | null;
    started_at: string;
  }[];
  balance: Record<string, number>;
  promos: { code: string; videos: number; created_at: string }[];
}
export interface Member {
  id: string;
  tg_id: number;
  username: string | null;
  role: 'owner' | 'viewer';
}
export interface PromoCode {
  id: string;
  code: string;
  videos: number;
  max_uses: number | null;
  expires_at: string | null;
  expired: boolean;
  note: string;
  active: boolean;
  created_at: string;
  author_username: string | null;
  author_tg_id: number;
  redemptions: number;
  made_video: number;
  spent: number;
  paid: number;
}
export interface PromoUser {
  id: string;
  tg_id: number;
  username: string | null;
  redeemed_at: string;
  spent: number;
  remaining: number;
  payments: number;
  first_paid_at: string | null;
}
export interface CampaignStats {
  recipients: number;
  pending: number;
  delivered: number;
  blocked: number;
  failed: number;
  skipped: number;
  opened: number;
  uploaded: number;
  rendered: number;
  replied: number;
  tests_delivered: number;
}
export interface Campaign extends CampaignStats {
  id: string;
  slug: string;
  text: string;
  button: string | null;
  video_note_file_id: string | null;
  segment: string;
  status: 'draft' | 'running' | 'paused' | 'done';
  send_from: number;
  send_to: number;
  pause_ms: number;
  usernames: string[] | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  author_username?: string | null;
  author_tg_id?: number;
  starter_username?: string | null;
  starter_tg_id?: number | null;
}
export interface TeamMember {
  id: string;
  tg_id: number;
  username: string | null;
  me: boolean;
}
export interface CampaignRecipient {
  id: number;
  status: 'pending' | 'sending' | 'delivered' | 'blocked' | 'error' | 'skipped';
  error: string | null;
  sent_at: string | null;
  test: boolean;
  user_id: string;
  tg_id: number;
  username: string | null;
  opened_at: string | null;
  uploaded_at: string | null;
  rendered_at: string | null;
  replies: { text: string; at: string }[];
}
export interface CampaignSegment {
  id: string;
  title: string;
  /** null — список ников: размер известен только после того, как их впишут. */
  count: number | null;
}
export interface TriggerCircle {
  key: string;
  title: string;
  when: string;
  hasVideo: boolean;
  enabled: boolean;
  enabledAt: string | null;
  delivered: number;
  blocked: number;
  failed: number;
}
